import React, { useState, useEffect } from 'react';
import { RefreshCw, Maximize2, Minimize2, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { buildApiUrl } from '../../../services/apiConfig';

const InitiativesTable = ({ apiHost = '54.204.131' }) => {
  const [initiativesData, setInitiativesData] = useState([]);
  const [isInitiativesLoading, setIsInitiativesLoading] = useState(false);
  const [initiativesSummaryData, setInitiativesSummaryData] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fsSortField, setFsSortField] = useState('ID');
  const [fsSortDir, setFsSortDir] = useState('asc');
  const [fsSearch, setFsSearch] = useState('');
  const [fsPageSize, setFsPageSize] = useState(10);
  const [fsPage, setFsPage] = useState(1);

  // Initiatives columns definition
  const initiativesColumns = [
    { label: "ID", key: "ID" },
    { label: "Domain", key: "Domain" },
    { label: "Use Case", key: "Use_Case" },
    { label: "Description", key: "Description" },
    { label: "Product Owner", key: "Product_Owner" },
    { label: "Current Stage", key: "Current_Stage" },
    { label: "Agents", key: "Agents" },
    { label: "Total Cost($)", key: "Total_Cost" },
    { label: "Monthly Cost($)", key: "Monthly_Cost" },
    { label: "Jira Link", key: "Jira_Link" },
    { label: "Data Usage Approval", key: "DURB" },
    { label: "AI Board Approval", key: "AIRB" },
    { label: "MRMG Risk Tier", key: "MRMG_Risk_Tier" },
    { label: "PtX Check", key: "PtX_Check" },
    { label: "MRMG Approved", key: "MRMG_Approved" },
  ];

  // Helper function to render status icons
  const renderStatusIcon = (status) => {
    if (status === "y") return '✓';
    if (status === 'n') return '✗';
    return status ? '✓' : '✗';
  };

  // Fetch initiatives data
  const fetchInitiativesData = async () => {
    setIsInitiativesLoading(true);
    try {
      const res = await fetch(buildApiUrl('SQL', '/run-sql'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: 'SELECT * FROM synthetic_data.dbo.initiatives'
        })
      });
      const data = await res.json();
      console.log("Initiatives Data:", data);
      const safeData = Array.isArray(data.result) ? data.result : [];
      setInitiativesData(safeData);
    } catch (err) {
      console.error("Error loading initiatives data:", err);
      setInitiativesData([]);
    } finally {
      setIsInitiativesLoading(false);
    }
  };

  // Fetch initiatives summary data
  const fetchInitiativesSummary = async () => {
    try {
      const res = await fetch(buildApiUrl('SQL', '/run-sql'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: `
            SELECT COUNT(*) AS [Total],
              COUNT(CASE WHEN Current_Stage NOT IN ('Production', 'In-Development', 'Pilot') THEN 1 END) AS [Initiated],
              COUNT(CASE WHEN DURB = 'n' THEN 1 END) AS [Pending_Data_Usage],
              COUNT(CASE WHEN Current_Stage = 'In-Development' THEN 1 END) AS [In_Development],
              COUNT(CASE WHEN MRMG_Approved = 'n' THEN 1 END) AS [Pending_MRMG],
              COUNT(CASE WHEN AIRB = 'n' THEN 1 END) AS [Pending_AI_Approval],
              COUNT(CASE WHEN PtX_Check = 'n' THEN 1 END) AS [Pending_PtX],
              COUNT(CASE WHEN Current_Stage = 'Production Beta' THEN 1 END) AS [Production_Beta],
              COUNT(CASE WHEN Current_Stage = 'Production' THEN 1 END) AS [Production],
              COUNT(CASE WHEN Current_Stage = 'Cancelled' THEN 1 END) AS [Cancelled]
            FROM synthetic_data.dbo.initiatives;
          `
        })
      });
      const data = await res.json();
      const row = data.result?.[0];
      if (row) {
        const formattedSummary = [
          { label: "Total", value: row.Total },
          { label: "Initiated", value: row.Initiated },
          { label: "Pending Data Usage", value: row.Pending_Data_Usage },
          { label: "In-Development", value: row.In_Development },
          { label: "Pending MRMG", value: row.Pending_MRMG },
          { label: "Pending AI Approval", value: row.Pending_AI_Approval },
          { label: "Pending PtX", value: row.Pending_PtX },
          { label: "Production Beta", value: row.Production_Beta },
          { label: "Production", value: row.Production },
          { label: "Cancelled", value: row.Cancelled }
        ];
        setInitiativesSummaryData(formattedSummary);
      }
    } catch (err) {
      console.error("Error loading initiatives summary:", err);
      setInitiativesSummaryData([]);
    }
  };

  useEffect(() => {
    fetchInitiativesData();
    fetchInitiativesSummary();
  }, []);

  // Function to refresh data
  const refreshData = () => {
    fetchInitiativesData();
    fetchInitiativesSummary();
  };

  const handleFsSort = (key) => {
    if (key === fsSortField) {
      setFsSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setFsSortField(key);
      setFsSortDir('asc');
    }
    setFsPage(1);
  };

  const COLORS = {
    'Total':               { bg: 'border-l-primaryDark'    },
    'Initiated':           { bg: 'border-l-primary'        },
    'In-Development':      { bg: 'border-l-secondary'      },
    'Pending Data Usage':  { bg: 'border-l-secondaryDark'  },
    'Pending MRMG':        { bg: 'border-l-primaryLight'   },
    'Pending AI Approval': { bg: 'border-l-secondaryLight' },
    'Pending PtX':         { bg: 'border-l-primaryDark'    },
    'Production Beta':     { bg: 'border-l-secondary'      },
    'Production':          { bg: 'border-l-primary'        },
    'Cancelled':           { bg: 'border-l-secondaryDark'  },
  };

  const totalItem  = initiativesSummaryData.find(i => i.label === 'Total');
  const otherItems = initiativesSummaryData.filter(i => i.label !== 'Total');

  // Fullscreen: filter → sort → paginate (all client-side)
  const NUMERIC_KEYS = ['Total_Cost', 'Monthly_Cost'];
  const fsFiltered = fsSearch
    ? initiativesData.filter(row =>
        initiativesColumns.some(({ key }) => {
          const val = row[key];
          return val != null && val.toString().toLowerCase().includes(fsSearch.toLowerCase());
        })
      )
    : initiativesData;
  const fsSorted = [...fsFiltered].sort((a, b) => {
    const aVal = a[fsSortField];
    const bVal = b[fsSortField];
    if (NUMERIC_KEYS.includes(fsSortField)) {
      return fsSortDir === 'asc' ? (aVal ?? 0) - (bVal ?? 0) : (bVal ?? 0) - (aVal ?? 0);
    }
    const aStr = (aVal ?? '').toString().toLowerCase();
    const bStr = (bVal ?? '').toString().toLowerCase();
    if (aStr < bStr) return fsSortDir === 'asc' ? -1 : 1;
    if (aStr > bStr) return fsSortDir === 'asc' ? 1 : -1;
    return 0;
  });
  const fsTotalPages = Math.max(1, Math.ceil(fsSorted.length / fsPageSize));
  const fsStart = (fsPage - 1) * fsPageSize;
  const fsPageRows = fsSorted.slice(fsStart, fsStart + fsPageSize);

  return (
    <>
      {/* Initiatives Summary Section */}
      <div className="bg-gray-50 py-4 mb-5">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-[14px] font-semibold text-gray-600 uppercase tracking-[0.7px]">Summary</h2>
          {isInitiativesLoading && (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
          {totalItem && (() => {
            const c = COLORS['Total'];
            return (
              <div className={`col-span-2 sm:col-span-1 sm:row-span-2 ${c.bg} p-3 sm:p-4 rounded-lg border border-gray-200 border-l-8 bg-white flex items-center justify-center flex-col transition-all duration-200`}>
                <div className="text-sm mb-1 text-gray-700">Total</div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-700">{totalItem.value}</div>
              </div>
            );
          })()}
          {otherItems.map((item, index) => {
            const c = COLORS[item.label] || { bg: 'border-l-primary' };
            return (
              <div key={index} className={`${c.bg} p-3 sm:p-4 rounded-lg border border-gray-200 border-l-8 bg-white flex flex-col items-center justify-center text-center transition-all duration-200`}>
                <div className="text-[11px] sm:text-xs mb-1 text-gray-700 font-semibold leading-tight">{item.label}</div>
                <div className="text-sm sm:text-base font-bold text-gray-700">{item.value}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Initiatives Table */}
      <div className="bg-gray-50 py-4 mb-5">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-[14px] font-semibold text-gray-600 uppercase tracking-[0.7px]">AI Initiatives</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={refreshData}
              disabled={isInitiativesLoading}
              className="flex items-center text-primary text-sm bg-transparent border-none cursor-pointer py-1 px-2 rounded transition-all duration-200 hover:bg-primaryLight disabled:text-primaryLight disabled:cursor-not-allowed"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={() => setIsFullscreen(true)}
              className="flex items-center text-primary text-sm bg-transparent border-none cursor-pointer py-1 px-2 rounded transition-all duration-200 hover:bg-primaryLight"
              title="Expand to fullscreen"
            >
              <Maximize2 size={16} />
            </button>
          </div>
        </div>
        
        {isInitiativesLoading ? (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-700">Loading initiatives data...</p>
          </div>
        ) : (
          <div className="overflow-x-auto scrollbar-none border border-gray-200 rounded-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  {initiativesColumns.map(({ label, key }) => (
                    <th key={key} className="py-3 px-4 text-left text-sm font-semibold text-primary uppercase tracking-wider border-b border-gray-200">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {initiativesData.length > 0 ? (
                  initiativesData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      {initiativesColumns.map(({ key }) => (
                        <td key={key} className={`py-3 px-4 text-sm text-gray-600 whitespace-nowrap ${key === "ID" ? "font-medium text-gray-900" : ""}`}>
                          {/* Check if the value is 'y' or 'n', then render status icon */}
                          {key === "Jira_Link" || key === "DURB" || key === "AIRB" || key === "Platform_Mnemonic" || key === "MRMG_Risk_Tier" || key === "PtX_Check" || key === "MRMG_Approved" ? (
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                              row[key] === 'y' ? 'bg-successLight text-success' : 
                              row[key] === 'n' ? 'bg-errorLight text-error' : 
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {renderStatusIcon(row[key])}
                            </span>
                          ) : key === "Current_Stage" ? (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              row[key] === 'Production' ? 'bg-successLight text-success' : 
                              row[key] === 'In-Development' ? 'bg-blue-100 text-primaryDark' : 
                              row[key] === 'Pilot' ? 'bg-purple-100 text-purple-800' : 
                              row[key] === 'Production Beta' ? 'bg-yellow-100 text-amber-700' : 
                              row[key] === 'Cancelled' ? 'bg-errorLight text-error' : 
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {row[key]}
                            </span>
                          ) : (
                            row[key] !== undefined ? row[key].toString() : ''
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={initiativesColumns.length} className="py-4 px-3 text-center text-sm text-gray-500">
                      No initiatives data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Fullscreen overlay */}
      {isFullscreen && (
        <div className="fixed top-0 left-0 md:left-16 right-0 bottom-0 z-[39] bg-white flex flex-col pt-[164px] md:pt-[116px]">

          {/* Heading row */}
          <div className="flex-none flex justify-between items-center px-4 sm:px-6 py-3 bg-white">
            <h2 className="text-[14px] font-semibold text-gray-600 uppercase tracking-[0.7px]">AI Initiatives</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={refreshData}
                disabled={isInitiativesLoading}
                className="flex items-center text-primary text-sm bg-transparent border-none cursor-pointer py-1 px-2 rounded transition-all duration-200 hover:bg-primaryLight disabled:text-primaryLight disabled:cursor-not-allowed"
              >
                <RefreshCw size={16} />
              </button>
              <button
                onClick={() => setIsFullscreen(false)}
                className="flex items-center text-primary text-sm bg-transparent border-none cursor-pointer py-1 px-2 rounded transition-all duration-200 hover:bg-primaryLight"
                title="Exit fullscreen"
              >
                <Minimize2 size={16} />
              </button>
            </div>
          </div>

          {/* Toolbar: search + rows per page */}
          <div className="flex-none flex flex-wrap items-center gap-3 px-4 sm:px-6 py-2 bg-white">
            <div className="flex items-center gap-2 flex-1 min-w-0 border border-gray-200 rounded px-2 py-1">
              <Search size={13} className="text-gray-400 shrink-0" />
              <input
                type="text"
                placeholder="Search…"
                value={fsSearch}
                onChange={e => { setFsSearch(e.target.value); setFsPage(1); }}
                className="w-full min-w-0 text-sm bg-transparent focus:outline-none text-gray-700 placeholder-gray-400"
              />
              {fsSearch && (
                <button onClick={() => { setFsSearch(''); setFsPage(1); }} className="text-gray-400 hover:text-gray-600 shrink-0">
                  <X size={13} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 shrink-0">
              <span className="hidden sm:inline">Rows per page:</span>
              <select
                value={fsPageSize}
                onChange={e => { setFsPageSize(Number(e.target.value)); setFsPage(1); }}
                className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* Scrollable table — border is on this element so it stays fixed */}
          <div className="flex-1 overflow-auto scrollbar-none mx-4 sm:mx-6 mb-3 border border-gray-200 rounded-lg">
            {isInitiativesLoading ? (
              <div className="flex flex-col items-center justify-center py-10">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-700">Loading initiatives data...</p>
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    {initiativesColumns.map(({ label, key }) => (
                      <th
                        key={key}
                        onClick={() => handleFsSort(key)}
                        className="py-3 px-3 text-left text-xs font-semibold text-primary uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                      >
                        <div className="flex items-center gap-1">
                          {label}
                          {fsSortField === key
                            ? (fsSortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
                            : null}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {fsPageRows.length > 0 ? (
                    fsPageRows.map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        {initiativesColumns.map(({ key }) => (
                          <td key={key} className={`py-3 px-4 text-sm text-gray-600 whitespace-nowrap ${key === "ID" ? "font-medium text-gray-900" : ""}`}>
                            {key === "Jira_Link" || key === "DURB" || key === "AIRB" || key === "Platform_Mnemonic" || key === "MRMG_Risk_Tier" || key === "PtX_Check" || key === "MRMG_Approved" ? (
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                                row[key] === 'y' ? 'bg-successLight text-success' :
                                row[key] === 'n' ? 'bg-errorLight text-error' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {renderStatusIcon(row[key])}
                              </span>
                            ) : key === "Current_Stage" ? (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                row[key] === 'Production' ? 'bg-successLight text-success' :
                                row[key] === 'In-Development' ? 'bg-blue-100 text-primaryDark' :
                                row[key] === 'Pilot' ? 'bg-purple-100 text-purple-800' :
                                row[key] === 'Production Beta' ? 'bg-yellow-100 text-amber-700' :
                                row[key] === 'Cancelled' ? 'bg-errorLight text-error' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {row[key]}
                              </span>
                            ) : (
                              row[key] !== undefined ? row[key].toString() : ''
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={initiativesColumns.length} className="py-4 px-3 text-center text-sm text-gray-500">
                        {fsSearch ? `No results for "${fsSearch}"` : 'No initiatives data available'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination footer */}
          <div className="flex-none flex items-center justify-between px-4 sm:px-6 py-2 border-t border-gray-200 bg-white text-sm text-gray-600">
            <span className="truncate mr-2">
              {fsSorted.length === 0
                ? '0 results'
                : `${fsStart + 1}–${Math.min(fsStart + fsPageSize, fsSorted.length)} of ${fsSorted.length}`}
            </span>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setFsPage(p => Math.max(1, p - 1))}
                disabled={fsPage === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="px-1 whitespace-nowrap hidden sm:inline">Page {fsPage} of {fsTotalPages}</span>
              <span className="px-1 sm:hidden">{fsPage}/{fsTotalPages}</span>
              <button
                onClick={() => setFsPage(p => Math.min(fsTotalPages, p + 1))}
                disabled={fsPage === fsTotalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

        </div>
      )}
    </>
  );
};

export default InitiativesTable;